import { useState } from 'react';
import cx from 'classnames';

import { ChevronIcon } from 'src/components/ChevronIcon';
import { CustomButtonBase } from 'src/components/CustomButtonBase';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ImageStyleList } from 'src/components/ImageStyleList';
import { StaticPanel } from 'src/components/StaticPanel';
import { ROTATE_DIRECTION } from 'src/data/ui';
import { useBooleanState } from 'src/lib/hooks/useBooleanState';
import { usePrepareImageStyleListProps } from 'src/services/usePrepareImageStyleListProps';

import { StylesMenu } from './components/StylesMenu';
import { useImageStyleName } from './services/useImageStyleName';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

export const BlockImageStyle = ({ className, classes, title, disabled, grayoutTitle }) => {
  const styles = useStyles({ classes });

  const [rootElement, setRootElement] = useState(null);
  const [menuOpened, openMenu, closeMenu] = useBooleanState(false);

  const { generalStyles, customStyles, onCreateStyle, onEditStyle, onDeleteStyle } =
    usePrepareImageStyleListProps();
  const { selectedImageStyleId } = useViewState();
  const selectedStyleName = useImageStyleName({
    customStyles,
    generalStyles,
    selectedImageStyleId,
  });

  const { handleDropdownClick, handleMenuClose, handleSelectStyle } = useHandlers({
    openMenu,
    closeMenu,
  });

  return (
    <StaticPanel
      className={cx(styles.root, className, { [styles.gray]: grayoutTitle })}
      classes={{ title: styles.title }}
      ref={setRootElement}
      title={title || 'Image style'}
    >
      <CustomButtonBase
        className={cx(styles.dropdown, {
          [styles.active]: menuOpened,
          [styles.empty]: selectedImageStyleId == null,
          [styles.disabled]: disabled,
        })}
        onClick={handleDropdownClick}
        disabled={disabled}
      >
        <CustomTypography
          className={styles.value}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {selectedStyleName || 'Select image style'}
        </CustomTypography>

        <ChevronIcon
          className={styles.icon}
          direction={menuOpened ? ROTATE_DIRECTION.right : ROTATE_DIRECTION.down}
        />
      </CustomButtonBase>

      <StylesMenu
        referenceElement={rootElement}
        open={menuOpened}
        onClose={handleMenuClose}
      >
        <ImageStyleList
          className={styles.stylesList}
          generalStyles={generalStyles}
          customStyles={customStyles}
          selectedId={selectedImageStyleId}
          onSelect={handleSelectStyle}
          onCreateStyle={onCreateStyle}
          onEditStyle={onEditStyle}
          onDeleteStyle={onDeleteStyle}
          disabled={disabled}
        />
      </StylesMenu>
    </StaticPanel>
  );
};
